"use client";

import { useState, useRef, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { useParams, usePathname as useRawPathname } from "next/navigation";
import { labels, routing } from "@/i18n/routing";
import { syncAccountLanguage } from "@/helpers/syncAccountLanguage";

export default function LanguageSelector() {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const locale = useLocale();
    const router = useRouter();
    const pathname = usePathname();
    const rawPathname = useRawPathname();
    const params = useParams();
    const t = useTranslations("layout");
    const { status } = useSession();

    const currentLanguage = labels.find((lang) => lang.id === locale);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleLanguageChange = (newLocale) => {
        if (newLocale === locale) {
            setIsOpen(false);
            return;
        }

        // Changer de langue est un geste explicite : pour un utilisateur
        // connecté, on aligne la langue du compte (e-mails, notifications).
        if (status === "authenticated") {
            syncAccountLanguage(newLocale);
        }

        // Utilise le router next-intl pour changer de langue en conservant
        // les params dynamiques de l'URL courante (ex. [slug]).
        try {
            router.push({ pathname, params }, { locale: newLocale });
        } catch (error) {
            // Filet de sécurité : page statique correspondant à une route dynamique
            // non déclarée dans i18n/routing → on bascule la locale sur le chemin réel
            // et on laisse le middleware next-intl rediriger vers la bonne version.
            const segments = rawPathname.split("/");
            if (routing.locales.includes(segments[1])) {
                segments[1] = newLocale;
            } else {
                segments.splice(1, 0, newLocale);
            }
            window.location.assign(segments.join("/") || `/${newLocale}`);
        }
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-grayDark-400 hover:text-gray-900 dark:hover:text-grayDark-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors rounded-md border border-gray-200 dark:border-grayDark-700 hover:border-gray-300 dark:hover:border-grayDark-600"
                aria-label={t("Footer_language_selector_aria")}
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <span className="text-base">
                    {currentLanguage?.label.split(" ")[0]}
                </span>
                <span className="text-xs font-medium">
                    {currentLanguage?.label.split(" ")[1]}
                </span>
                <svg
                    className={`w-4 h-4 transition-transform ${
                        isOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {isOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-full min-w-[140px] bg-white dark:bg-grayDark-800 border border-gray-200 dark:border-grayDark-700 rounded-md shadow-lg z-50">
                    <div className="py-1">
                        {labels.map((language) => (
                            <button
                                key={language.id}
                                onClick={() =>
                                    handleLanguageChange(language.id)
                                }
                                className={`w-full px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-grayDark-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center gap-2 ${
                                    language.id === locale
                                        ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                                        : "text-gray-700 dark:text-grayDark-300"
                                }`}
                            >
                                <span className="text-base">
                                    {language.label.split(" ")[0]}
                                </span>
                                <span className="text-xs font-medium">
                                    {language.label.split(" ")[1]}
                                </span>
                                {language.id === locale && (
                                    <svg
                                        className="w-3 h-3 ml-auto text-blue-600 dark:text-blue-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
